import React, { useEffect, useState } from "react";
import { firestore, storage } from "../../firebase-config";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import "../../styles/manageplacement.css";

const Placements = () => {
  const [studentName, setStudentName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [studentImage, setStudentImage] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [newImage, setNewImage] = useState(null);
  const [newLogo, setNewLogo] = useState(null);

  // Fetch all placements
  const fetchPlacements = async () => {
    try {
      const querySnapshot = await getDocs(collection(firestore, "placements"));
      const placementsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPlacements(placementsData);
    } catch (error) {
      setErrorMessage("Error fetching placements.");
    }
  };

  useEffect(() => {
    fetchPlacements();
  }, []);

  // Upload new placement
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // If editing, call handleUpdate instead
    if (editingPlacement) {
      await handleUpdate(event);
      return;
    }
    
    if (!studentImage) {
      setErrorMessage("Please select a student image.");
      return;
    }

    setLoading(true);
    
    try {
      // Upload student image
      const imageRef = ref(storage, `placements/${studentImage.name}`);
      const uploadTask = uploadBytesResumable(imageRef, studentImage);
      
      const imageUrl = await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          null,
          reject,
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      let logoUrl = null;
      let logoPath = null;

      // Upload company logo if provided
      if (companyLogo) {
        const logoRef = ref(storage, `company-logos/${companyLogo.name}`);
        const logoUploadTask = uploadBytesResumable(logoRef, companyLogo);
        
        logoUrl = await new Promise((resolve, reject) => {
          logoUploadTask.on(
            "state_changed",
            null,
            reject,
            async () => {
              const url = await getDownloadURL(logoUploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });
        logoPath = `company-logos/${companyLogo.name}`;
      }

      // Save to Firestore
      const docRef = await addDoc(collection(firestore, "placements"), {
        studentName,
        companyName,
        imageUrl,
        storagePath: `placements/${studentImage.name}`,
        companyLogo: logoUrl,
        logoStoragePath: logoPath,
        timestamp: new Date(),
      });

      setPlacements([
        ...placements,
        {
          id: docRef.id,
          studentName,
          companyName,
          imageUrl,
          storagePath: `placements/${studentImage.name}`,
          companyLogo: logoUrl,
          logoStoragePath: logoPath,
        },
      ]);
      
      setSuccessMessage("Placement data uploaded successfully!");
      setStudentName("");
      setCompanyName("");
      setStudentImage(null);
      setCompanyLogo(null);
      // Reset file inputs
      document.querySelector('input[type="file"]').value = null;
      const logoInput = document.querySelectorAll('input[type="file"]')[1];
      if (logoInput) logoInput.value = null;
    } catch (error) {
      setErrorMessage("Error uploading data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete placement
  const handleDelete = async (id, storagePath, logoStoragePath) => {
    try {
      // Delete student image
      const imageRef = ref(storage, storagePath);
      await deleteObject(imageRef);
      
      // Delete company logo if exists
      if (logoStoragePath) {
        const logoRef = ref(storage, logoStoragePath);
        await deleteObject(logoRef).catch(() => {
          console.log("Logo already deleted or doesn't exist");
        });
      }
      
      await deleteDoc(doc(firestore, "placements", id));
      setPlacements(placements.filter((placement) => placement.id !== id));
      setSuccessMessage("Placement deleted successfully.");
    } catch (error) {
      setErrorMessage("Error deleting placement: " + error.message);
    }
  };

  // Edit placement details
  const handleEdit = (placement) => {
    // Scroll to top to see the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setEditingPlacement(placement);
    setStudentName(placement.studentName || "");
    setCompanyName(placement.companyName || "");
    setStudentImage(null);
    setCompanyLogo(null);
    setNewImage(null);
    setNewLogo(null);
    setSuccessMessage("");
    setErrorMessage("");
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPlacement(null);
    setNewImage(null);
    setNewLogo(null);
    setStudentName("");
    setCompanyName("");
    setStudentImage(null);
    setCompanyLogo(null);
    setSuccessMessage("");
    setErrorMessage("");
  };

  // Update placement details
  const handleUpdate = async (event) => {
    if (event) event.preventDefault();
    
    if (!editingPlacement) return;

    setLoading(true);
    
    try {
      let updatedImageUrl = editingPlacement.imageUrl;
      let newStoragePath = editingPlacement.storagePath;
      let updatedLogoUrl = editingPlacement.companyLogo;
      let newLogoPath = editingPlacement.logoStoragePath;

      // Upload new student image if provided
      if (newImage) {
        const imageRef = ref(storage, `placements/${newImage.name}`);
        const uploadTask = uploadBytesResumable(imageRef, newImage);
        
        updatedImageUrl = await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            null,
            reject,
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });
        newStoragePath = `placements/${newImage.name}`;

        // Delete old image
        const oldImageRef = ref(storage, editingPlacement.storagePath);
        await deleteObject(oldImageRef);
      }

      // Upload new company logo if provided
      if (newLogo) {
        const logoRef = ref(storage, `company-logos/${newLogo.name}`);
        const logoUploadTask = uploadBytesResumable(logoRef, newLogo);
        
        updatedLogoUrl = await new Promise((resolve, reject) => {
          logoUploadTask.on(
            "state_changed",
            null,
            reject,
            async () => {
              const url = await getDownloadURL(logoUploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });
        newLogoPath = `company-logos/${newLogo.name}`;

        // Delete old logo if exists
        if (editingPlacement.logoStoragePath) {
          const oldLogoRef = ref(storage, editingPlacement.logoStoragePath);
          await deleteObject(oldLogoRef).catch(() => {
            console.log("Old logo already deleted or doesn't exist");
          });
        }
      }

      // Update Firestore document
      await updateDoc(doc(firestore, "placements", editingPlacement.id), {
        studentName,
        companyName,
        imageUrl: updatedImageUrl,
        storagePath: newStoragePath,
        companyLogo: updatedLogoUrl,
        logoStoragePath: newLogoPath,
      });

      // Update UI
      setPlacements(
        placements.map((placement) =>
          placement.id === editingPlacement.id
            ? {
                ...placement,
                studentName,
                companyName,
                imageUrl: updatedImageUrl,
                storagePath: newStoragePath,
                companyLogo: updatedLogoUrl,
                logoStoragePath: newLogoPath,
              }
            : placement
        )
      );

      setSuccessMessage("Placement updated successfully!");
      setEditingPlacement(null);
      setNewImage(null);
      setNewLogo(null);
      setStudentName("");
      setCompanyName("");
    } catch (error) {
      setErrorMessage("Error updating placement: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2>{editingPlacement ? "Edit Placement Data" : "Upload Placement Data"}</h2>

      <form onSubmit={handleSubmit}>
        <div>
          <label>Student Name:</label>
          <input
            type="text"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            required
          />
        </div>

        <div>
          <label>Company Name:</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
          />
        </div>

        <div>
          <label>Upload Student Image:</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              editingPlacement
                ? setNewImage(e.target.files[0])
                : setStudentImage(e.target.files[0])
            }
            required={!editingPlacement}
          />
          {editingPlacement && !newImage && (
            <p style={{ fontSize: "0.9em", color: "#666" }}>
              Leave empty to keep current image
            </p>
          )}
        </div>

        <div>
          <label>Upload Company Logo:</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              editingPlacement
                ? setNewLogo(e.target.files[0])
                : setCompanyLogo(e.target.files[0])
            }
          />
          {editingPlacement && !newLogo && (
            <p style={{ fontSize: "0.9em", color: "#666" }}>
              Leave empty to keep current logo
            </p>
          )}
        </div>

        <button type="submit" disabled={loading}>
          {loading ? "Processing..." : editingPlacement ? "Update" : "Submit"}
        </button>
        
        {editingPlacement && (
          <button
            type="button"
            onClick={handleCancelEdit}
            style={{ marginLeft: "10px" }}
          >
            Cancel
          </button>
        )}
      </form>

      {successMessage && <p style={{ color: "green" }}>{successMessage}</p>}
      {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

      <h2>Manage Placements</h2>
      <div className="placement-list">
        {placements.length > 0 ? (
          placements.map((placement) => (
            <div key={placement.id} className="placement-card">
              <img
                src={placement.imageUrl}
                alt={placement.studentName}
                width="150"
                height="150"
              />
              <p>Name: {placement.studentName}</p>
              <p>Company: {placement.companyName}</p>
              {placement.companyLogo && (
                <div style={{ marginTop: "10px" }}>
                  <img
                    src={placement.companyLogo}
                    alt="Company Logo"
                    style={{ maxWidth: "100px", maxHeight: "50px", objectFit: "contain" }}
                  />
                </div>
              )}
              <button onClick={() => handleEdit(placement)}>Edit</button>
              <button
                onClick={() => handleDelete(placement.id, placement.storagePath, placement.logoStoragePath)}
              >
                Delete
              </button>
            </div>
          ))
        ) : (
          <p>No placements available.</p>
        )}
      </div>
    </div>
  );
};

export default Placements;